import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout, updateLastActiveTime} from '@/redux/sessionSlice';
import { buildRoutes } from '@/service/buildRoutes';
import localStorageKeys from '@/constants/localStorageKeys';
import { setStringInBrowser } from '@/utils/browserStorage';

const SESSION_TIMEOUT = 30*60*1000;
const MIN_ACTIVITY_UPDATE_MS = 10000; 

export default function SessionManager() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const wasVideoPlayingRef = useRef(false);

  const { lastActiveTime, isVideoPlaying } = useSelector(state => state.session);
  const authState = useSelector(state => state.authState);
  
  const actuallyLoggedIn = Boolean(authState?.userToken && authState?.userInfo);

  useEffect(() => {
    if (wasVideoPlayingRef.current && !isVideoPlaying) {
      dispatch(updateLastActiveTime());
    }
    wasVideoPlayingRef.current = isVideoPlaying;
  }, [isVideoPlaying, dispatch]);

  useEffect(() => {
    if (!actuallyLoggedIn || !lastActiveTime) return;
    if (location.pathname.startsWith('/AdminDashboard')) return;

    const now = Date.now();
    const elapsed = now - lastActiveTime;
    const remaining = Math.max(SESSION_TIMEOUT - elapsed, 0);

    if (remaining <= 0 && !isVideoPlaying) {
      setStringInBrowser(localStorageKeys.REDIRECT_AFTER_LOGIN, location.pathname + location.search);
      dispatch(logout());
      navigate(buildRoutes.toLogin(), {
        replace: true,
        state: { from: location.pathname + location.search },
      });
      return;
    }

    const timeout = setTimeout(() => {
      if (!isVideoPlaying) {
        setStringInBrowser(localStorageKeys.REDIRECT_AFTER_LOGIN, location.pathname + location.search);
        dispatch(logout());
        navigate(buildRoutes.toLogin(), {
          replace: true,
          state: { from: location.pathname + location.search },
        });
      }
    }, remaining);

    return () => clearTimeout(timeout);
  }, [actuallyLoggedIn, lastActiveTime, isVideoPlaying, dispatch, navigate, location.pathname, location.search]);

  useEffect(() => {
    let lastSent = 0;
    const updateActivity = () => {
      const now = Date.now();
      if (now - lastSent < MIN_ACTIVITY_UPDATE_MS) return;
      lastSent = now;
      dispatch(updateLastActiveTime());
    };

    window.addEventListener('mousemove', updateActivity);
    window.addEventListener('keydown', updateActivity);
    window.addEventListener('click', updateActivity);
    window.addEventListener('scroll', updateActivity);

    return () => {
      window.removeEventListener('mousemove', updateActivity);
      window.removeEventListener('keydown', updateActivity);
      window.removeEventListener('click', updateActivity);
      window.removeEventListener('scroll', updateActivity);
    };
  }, [dispatch]);

  return null;
}
